import { compose } from '@reduxjs/toolkit';

import type { Middleware } from './rtk-types';
import type { GetStoreFn, InjectMiddlewareFn } from './types';

export function createDynamicMiddleware(getStore: GetStoreFn) {
    /**
     * Список мидлвар, которые были подключены к стору в рантайме.
     */
    const dynamicMiddlewares: Middleware[] = [];

    /**
     * Добавляет мидлвару в модульный стор.
     *
     * @param middleware – Мидлвара, которую нужно добавить.
     */
    const injectMiddleware: InjectMiddlewareFn = middleware => {
        dynamicMiddlewares.push(middleware);
    };

    /**
     * Redux-мидлвара, которая запускает все мидлвары сохраненные в dynamicMiddlewares.
     * Нужна, чтобы иметь возможность добавлять новые мидлвары в рантайме.
     *
     * Композиция кешируется в замыкании конкретного инстанса стора. Так каждый стор работает
     * со своим набором применённых мидлвар, привязанных к его собственному mwApi.
     */
    const dynamicMiddleware: Middleware = api => {
        let composedDynamicMiddleware: ReturnType<ReturnType<Middleware>> | null = null;
        let lastComposedMiddlewareIndex = -1;

        return next => action => {
            getStore();

            if (lastComposedMiddlewareIndex < dynamicMiddlewares.length - 1) {
                const middlewaresToCompose = dynamicMiddlewares.slice(lastComposedMiddlewareIndex + 1);
                const appliedMiddlewaresToCompose = middlewaresToCompose.map(middleware => middleware(api));

                // Мидлвары композируются в обратном порядке: `compose(m2, m1)(next) => m2(m1(next))`.
                // Последняя добавленная мидлвара должна быть самой внешней - необходим `reverse`.
                appliedMiddlewaresToCompose.reverse();

                composedDynamicMiddleware = compose<ReturnType<ReturnType<Middleware>>>(...appliedMiddlewaresToCompose)(
                    composedDynamicMiddleware ?? next
                );
                lastComposedMiddlewareIndex = dynamicMiddlewares.length - 1;
            }

            return composedDynamicMiddleware ? composedDynamicMiddleware(action) : next(action);
        };
    };

    return { dynamicMiddleware, injectMiddleware };
}
